<template>
  <div class="container-fluid">
        <section>
          <div class="py-8">
            <div class="d-flex flex-wrap align-items-center justify-content-between">
              <div class="col-12 col-lg-auto mb-4 mb-lg-0">
                <div class="d-flex align-items-center">
                  <h4 class="mb-0 me-2">{{$t('insights')}}</h4>
                  <!-- <button 
                    class="btn btn-sm btn-outline-secondary ms-2" 
                    @click="showSettings = !showSettings"
                    :title="$t('customize_dashboard')"
                  >
                    <AppIcon name="bi-gear" class="bi bi-gear" />
                  </button> -->
                </div>
              </div>
              <div class="col-12 col-lg-4">
                <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select">
                  <option value="">{{$t('filter_by_date')}}</option>
                  <option value="today">{{$t('today')}}</option>
                  <option value="yesterday">{{$t('yesterday')}}</option>
                  <option value="this_week">{{$t('this_week')}}</option>
                  <option value="last_week">{{$t('last_week')}}</option>
                  <option value="this_month">{{$t('this_month')}}</option>
                  <option value="custom">{{$t('custom_date')}}</option>
                </select>
              </div>
              <div class="col-12 col-lg-3" v-if="dateRangeOption === 'custom'">
                <date-picker 
                  :clearable="true" 
                  :editable="false" 
                  class="w-100" 
                  v-model="range" 
                  type="date" 
                  range 
                  :multi-calendars="1"
                  :placeholder="$t('select_date_range')"
                ></date-picker>
              </div>
            </div>
            
            <!-- Settings Panel -->
            <div v-if="showSettings" class="card mt-3 mb-3 shadow-sm">
              <div class="card-body">
                <h6 class="card-title mb-3"><AppIcon name="bi-sliders" class="bi bi-sliders me-2" />{{$t('customize_dashboard')}}</h6>
                <div class="row">
                  <div class="col-md-3 col-sm-6 mb-2">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" v-model="visibleCards.totalSales" id="toggleSales">
                      <label class="form-check-label small" for="toggleSales">{{$t('total_sales')}}</label>
                    </div>
                  </div>
                  <div class="col-md-3 col-sm-6 mb-2">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" v-model="visibleCards.totalOrders" id="toggleOrders">
                      <label class="form-check-label small" for="toggleOrders">{{$t('total_orders')}}</label>
                    </div>
                  </div>
                  <div class="col-md-3 col-sm-6 mb-2">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" v-model="visibleCards.dailySales" id="toggleDaily">
                      <label class="form-check-label small" for="toggleDaily">{{$t('daily_sales_trend')}}</label>
                    </div>
                  </div>
                  <div class="col-md-3 col-sm-6 mb-2">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" v-model="visibleCards.topProducts" id="toggleProducts">
                      <label class="form-check-label small" for="toggleProducts">{{$t('sales_by_product')}}</label>
                    </div>
                  </div>
                  <div class="col-md-3 col-sm-6 mb-2">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" v-model="visibleCards.paymentMethods" id="togglePayment">
                      <label class="form-check-label small" for="togglePayment">{{$t('orders_by_payment_method')}}</label>
                    </div>
                  </div>
                  <div class="col-md-3 col-sm-6 mb-2">
                    <div class="form-check form-switch">
                      <input class="form-check-input" type="checkbox" v-model="visibleCards.canceledOrders" id="toggleCanceled">
                      <label class="form-check-label small" for="toggleCanceled">{{$t('canceled_orders')}}</label>
                    </div>
                  </div>
                </div>
                <div class="mt-3 text-end">
                  <button 
                    class="btn btn-primary"
                    @click="saveDashboardSettings"
                  >
                    <AppIcon name="bi-save" class="bi bi-save me-1" /> {{$t('save')}}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
    
    <!-- Metrics Cards -->
    <div class="row mb-3">
      <!-- Total Sales -->
      <InsightCard
        :visible="visibleCards.totalSales"
        :title="$t('total_sales')"
        icon="currency-dollar"
        container-class="col-lg-4 col-md-6 mb-3"
        @refresh="fetchTotalSales"
      >
        <template #trend>
          <div v-if="totalSales.previous_total_sales !== undefined">
            <span :class="getChangeColor(totalSales.total_sales, totalSales.previous_total_sales)">
              <AppIcon :name="getChangeIcon(totalSales.total_sales, totalSales.previous_total_sales)" :class="getChangeIcon(totalSales.total_sales, totalSales.previous_total_sales)" />
              {{ getChangePercent(totalSales.total_sales, totalSales.previous_total_sales) }}%
            </span>
          </div>
        </template>
        <div v-if="totalSales.total_sales !== undefined">
          <h5 class="fw-bold text-success mb-0">{{ formatMoney(totalSales.total_sales) }}</h5>
        </div>
        <div v-else class="text-center">
          <p class="mb-0 text-muted small">No Data</p>
        </div>
      </InsightCard>
      
      <!-- Total Orders -->
      <InsightCard
        :visible="visibleCards.totalOrders"
        :title="$t('total_orders')"
        icon="receipt"
        container-class="col-lg-4 col-md-6 mb-3"
        @refresh="fetchTotalOrders"
      >
        <template #trend>
          <div v-if="totalOrders.previous_total_orders !== undefined">
            <span :class="getChangeColor(totalOrders.total_orders, totalOrders.previous_total_orders)">
              <AppIcon :name="getChangeIcon(totalOrders.total_orders, totalOrders.previous_total_orders)" :class="getChangeIcon(totalOrders.total_orders, totalOrders.previous_total_orders)" />
              {{ getChangePercent(totalOrders.total_orders, totalOrders.previous_total_orders) }}%
            </span>
          </div>
        </template>
        <div v-if="totalOrders.total_orders !== undefined">
          <h5 class="fw-bold text-primary mb-0">{{ totalOrders.total_orders }}</h5>
        </div>
        <div v-else class="text-center">
          <p class="mb-0 text-muted small">No Data</p>
        </div>
      </InsightCard>

      <!-- Canceled Orders -->
      <InsightCard
        :visible="visibleCards.canceledOrders"
        :title="$t('canceled_orders')"
        icon="cart-x"
        container-class="col-lg-4 col-md-12 mb-3"
        @refresh="fetchCanceledOrders"
      >
        <template #trend>
          <div v-if="canceledOrders.previous_total_canceled !== undefined">
            <span :class="getChangeColor(canceledOrders.total_canceled, canceledOrders.previous_total_canceled, true)">
              <AppIcon :name="getChangeIcon(canceledOrders.total_canceled, canceledOrders.previous_total_canceled)" :class="getChangeIcon(canceledOrders.total_canceled, canceledOrders.previous_total_canceled)" />
              {{ getChangePercent(canceledOrders.total_canceled, canceledOrders.previous_total_canceled) }}%
            </span>
          </div>
        </template>
        <div v-if="canceledOrders.total_canceled !== undefined">
          <h5 class="fw-bold text-danger mb-0">{{ canceledOrders.total_canceled }}</h5>
        </div>
        <div v-else class="text-center">
          <p class="mb-0 text-muted small">No Data</p>
        </div>
      </InsightCard>
    </div>
    
    <!-- Daily Sales Trend -->
    <div class="row mb-3">
      <InsightCard
        :visible="visibleCards.dailySales"
        :title="$t('daily_sales_trend')"
        icon="graph-up"
        container-class="col-12"
        @refresh="fetchDailySalesTrend"
      >
        <canvas id="dailySalesChart" width="400" height="100"></canvas>
      </InsightCard>
    </div>
    
    <!-- Charts Row: Sales by Product and Orders by Payment Method -->
    <div class="row mb-3">
      <!-- Sales by Product -->
      <InsightCard
        :visible="visibleCards.topProducts"
        :title="$t('sales_by_product')"
        icon="box-seam"
        container-class="col-lg-6 col-md-12"
        @refresh="fetchTopSellingProducts"
      >
        <div v-if="topSellingProducts.length > 0">
          <canvas id="topProductsChart" width="400" height="250"></canvas>
        </div>
        <div v-else class="text-center py-3">
          <p class="mt-2 mb-0 small">No Data Found</p>
        </div>
      </InsightCard>

      <!-- Orders by Payment Method -->
      <InsightCard
        :visible="visibleCards.paymentMethods"
        :title="$t('orders_by_payment_method')"
        icon="pie-chart"
        container-class="col-lg-6 col-md-12"
        @refresh="fetchOrdersByPayment"
      >
        <div v-if="ordersByPayment.length > 0">
          <canvas id="paymentMethodChart" width="400" height="250"></canvas>
        </div>
        <div v-else class="text-center py-3">
          <p class="mt-2 mb-0 small">No Data Found</p>
        </div>
      </InsightCard>
    </div>
    

  </div>
</template>

<script>
import helper from '../../helper';
import DatePicker from 'vue2-datepicker';
import moment from 'moment';
import Chart from 'chart.js';
import InsightCard from '../../components/InsightCard.vue';
import 'vue2-datepicker/index.css';

export default {
  name: 'PosInsights',
  persistedFilterState: {
    key: 'pos-insights',
    fields: ['dateRangeOption', 'range', 'start', 'end'],
    dateFields: ['range'],
  },
  components: { DatePicker, InsightCard },
  data() {
    return {
      topSellingProducts: [],
      totalSales: {},
      totalOrders: {},
      canceledOrders: {},
      ordersByPayment: [],
      dailySalesTrend: [],
      isLoading: true,
      range: [new Date(), new Date()],
      // Chart instances
      paymentChart: null,
      dailySalesChart: null,
      topProductsChart: null,
      dateRangeOption: '',
      start: moment().format('YYYY-MM-DD'),
      end: moment().format('YYYY-MM-DD'),
      debounceTimer: null,

      // Settings panel
      showSettings: false,
      // Visibility controls for insight cards
      visibleCards: {
        totalSales: true,
        totalOrders: true,
        dailySales: true,
        topProducts: true,
        canceledOrders: true,
        totalProfit: false,
        averageOrderValue: false,
        topCashiers: false,
        paymentMethods: true,
        refunds: false
      }
    };
  },
  methods: {
    formatMoney(money) {
      return helper.formatMoney(money);
    },
    
    // Optimized: Consolidated API URL builder
    buildUrl(endpoint) {
      let url = `${process.env.VUE_APP_API}/pos/${endpoint}`;
      if (this.start && this.end) {
        url += `?start=${this.start}&end=${this.end}`;
      }
      return url;
    },
    
    // Optimized: Generic fetch method with error handling
    async fetchData(endpoint, errorMsg, dataKey = null) {
      try {
        const url = this.buildUrl(endpoint);
        const response = await helper.customGet(url);
        return response.data;
      } catch (error) {
        this.$toast.error(errorMsg);
        return dataKey ? {} : [];
      }
    },
    
    async fetchTopSellingProducts() {
      this.topSellingProducts = await this.fetchData(
        'top-selling-products',
        'Failed to load top selling products',
        false
      );
      this.renderTopProductsChart();
    },

    async fetchTotalSales() {
      this.totalSales = await this.fetchData(
        'total-sales',
        'Failed to load total sales',
        true
      );
    },

    async fetchTotalOrders() {
      this.totalOrders = await this.fetchData(
        'total-orders',
        'Failed to load total orders',
        true
      );
    },
    
    async fetchDailySalesTrend() {
      this.dailySalesTrend = await this.fetchData(
        'daily-sales-trend',
        'Failed to load daily sales trend',
        false
      );
      this.renderDailySalesChart();
    },

    async fetchOrdersByPayment() {
      this.ordersByPayment = await this.fetchData(
        'sales-by-payment',
        'Failed to load payment method data',
        false
      );
      this.renderPaymentChart();
    },

    async fetchCanceledOrders() {
      this.canceledOrders = await this.fetchData(
        'total-canceled',
        'Failed to load canceled orders',
        true
      );
    },

    getChangePercent(current, previous) {
      if (!previous || previous === 0) return current > 0 ? 100 : 0;
      const change = ((current - previous) / previous) * 100;
      return Math.abs(change).toFixed(1);
    },

    getChangeColor(current, previous, isNegativeMetric = false) {
      const change = current - previous;
      if (change === 0) return 'text-muted small';
      const isPositiveChange = change > 0;
      
      if (isNegativeMetric) {
        return isPositiveChange ? 'text-danger small fw-bold' : 'text-success small fw-bold';
      }
      return isPositiveChange ? 'text-success small fw-bold' : 'text-danger small fw-bold';
    },

    getChangeIcon(current, previous) {
      const change = current - previous;
      if (change === 0) return 'bi bi-dash';
      return change > 0 ? 'bi bi-arrow-up' : 'bi bi-arrow-down';
    },
    
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value);
    },
    
    getDateParams() {
      if (this.range && this.range.length === 2) {
        return {
          start_date: this.formatDate(this.range[0]),
          end_date: this.formatDate(this.range[1])
        };
      }
      return {};
    },
    
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    
    // Handle date range selection
    handleDateRangeChange() {
      const today = new Date();
      
      if (this.dateRangeOption === '') {
        this.range = [];
        this.start = null;
        this.end = null;
        this.debouncedRefresh();
        return;
      }
      
      // Calculate date ranges more efficiently
      const dateRanges = {
        today: [today, today],
        yesterday: (() => {
          const yesterday = new Date(today.getTime() - 86400000);
          return [yesterday, yesterday];
        })(),
        this_week: [new Date(today.getTime() - 604800000), today],
        last_week: (() => {
          const weekAgo = new Date(today.getTime() - 604800000);
          const twoWeeksAgo = new Date(today.getTime() - 1209600000);
          return [twoWeeksAgo, weekAgo];
        })(),
        this_month: [new Date(today.getFullYear(), today.getMonth(), 1), today]
      };
      
      if (dateRanges[this.dateRangeOption]) {
        this.range = dateRanges[this.dateRangeOption];
      } else if (this.dateRangeOption === 'custom') {
        this.range = [];
      }
    },

    loadDashboardSettings() {
        const metadata = this.business?.metadata || {};
        if (metadata.pos_settings) {
          this.visibleCards = metadata.pos_settings;
        }
    },

    async saveDashboardSettings() {
      const metadata = this.business?.metadata || {};
      metadata.pos_settings = { ...this.visibleCards };
      
      // Save to backend
      await helper.customPost(`${process.env.VUE_APP_API}/businesses`, {
          metadata: metadata
      });
      this.$toast.success('Dashboard settings saved successfully.');
    },
    
    // Optimized: Debounced refresh to prevent excessive API calls
    debouncedRefresh() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.refreshAllData();
      }, 300);
    },
    
    handleDateChange() {
      this.debouncedRefresh();
    },
    
    // Optimized: Batch all API calls with Promise.all
    async refreshAllData() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.fetchTopSellingProducts(),
          this.fetchTotalSales(),
          this.fetchTotalOrders(),
          this.fetchDailySalesTrend(),
          this.fetchOrdersByPayment(),
          this.fetchCanceledOrders()
        ]);
      } catch (error) {
        alert('Error refreshing data: ' + error.message);
      } finally {
        this.isLoading = false;
      }
    },
    
    // Optimized: Destroy chart before creating new one
    destroyChart(chartInstance) {
      if (chartInstance) {
        chartInstance.destroy();
      }
    },

    // Orders by Payment Method chart
    renderPaymentChart() {
      this.$nextTick(() => {
        const ctx = document.getElementById('paymentMethodChart');
        if (!ctx || !this.ordersByPayment.length) return;
        
        this.destroyChart(this.paymentChart);
        
        const labels = this.ordersByPayment.map(item => item.payment_method || 'Other');
        const data = this.ordersByPayment.map(item => parseInt(item.total_orders) || 0);
        
        // Vibrant color palette
        const colors = [
          '#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', 
          '#5a5c69', '#fd7e14', '#6610f2', '#6f42c1', '#e83e8c'
        ];
        
        this.paymentChart = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: labels,
            datasets: [{
              data: data,
              backgroundColor: colors.slice(0, data.length),
              hoverBackgroundColor: colors.slice(0, data.length),
              hoverBorderColor: "rgba(234, 236, 244, 1)",
            }]
          },
          options: {
            maintainAspectRatio: false,
            tooltips: {
              backgroundColor: "rgb(255,255,255)",
              bodyFontColor: "#858796",
              borderColor: '#dddfeb',
              borderWidth: 1,
              xPadding: 15,
              yPadding: 15,
              displayColors: false,
              caretPadding: 10,
            },
            legend: {
              display: true,
              position: 'bottom',
              labels: {
                usePointStyle: true,
                padding: 15
              }
            },
            cutoutPercentage: 70,
          },
        });
      });
    },
    

    
    // Optimized: Daily sales chart with better performance
    renderDailySalesChart() {
      this.$nextTick(() => {
        const ctx = document.getElementById('dailySalesChart');
        if (!ctx || !this.dailySalesTrend.length) return;
        
        this.destroyChart(this.dailySalesChart);
        
        const isOneDay = this.start === this.end;
        const labels = this.dailySalesTrend.map(item => {
          if (isOneDay) {
            const hour = parseInt(item.date);
            return isNaN(hour) ? item.date : `${String(hour).padStart(2, '0')}:00`;
          }
          return moment(item.date).format('MMM DD');
        });
        const salesData = this.dailySalesTrend.map(item => parseFloat(item.total_sales) || 0);
        const ordersData = this.dailySalesTrend.map(item => parseInt(item.total_orders) || 0);
        
        // Flat color palette
        const flatColors = {
          primary: '#3498db',
          secondary: '#2ecc71',
          accent: '#e74c3c',
          lightGray: '#ecf0f1',
          darkGray: '#7f8c8d'
        };
        
        this.dailySalesChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: labels,
            datasets: [{
              label: this.$t('sales_amount'),
              data: salesData,
              borderColor: flatColors.primary,
              backgroundColor: `${flatColors.primary}33`,
              borderWidth: 3,
              pointBackgroundColor: '#fff',
              pointBorderColor: flatColors.primary,
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointHoverBackgroundColor: flatColors.primary,
              pointHoverBorderColor: '#fff',
              pointHoverBorderWidth: 2,
              fill: true,
              tension: 0.3,
              yAxisID: 'y-axis-1'
            }, {
              label: this.$t('number_of_orders'),
              data: ordersData,
              borderColor: flatColors.secondary,
              backgroundColor: `${flatColors.secondary}33`,
              borderWidth: 3,
              borderDash: [5, 5],
              pointBackgroundColor: '#fff',
              pointBorderColor: flatColors.secondary,
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
              pointHoverBackgroundColor: flatColors.secondary,
              pointHoverBorderColor: '#fff',
              pointHoverBorderWidth: 2,
              fill: false,
              tension: 0.3,
              yAxisID: 'y-axis-2'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            layout: {
              padding: {
                top: 20,
                right: 20,
                bottom: 10,
                left: 10
              }
            },
            scales: {
              xAxes: [{
                gridLines: {
                  display: true,
                  color: flatColors.lightGray,
                  drawBorder: false
                },
                ticks: {
                  fontColor: flatColors.darkGray,
                  fontSize: 12,
                  padding: 10
                }
              }],
              yAxes: [{
                id: 'y-axis-1',
                type: 'linear',
                position: 'left',
                gridLines: {
                  color: flatColors.lightGray,
                  drawBorder: false,
                  zeroLineColor: flatColors.lightGray
                },
                ticks: {
                  fontColor: flatColors.primary,
                  fontSize: 12,
                  padding: 10,
                  callback: (value) => this.formatMoney(value)
                },
                scaleLabel: {
                  display: true,
                  labelString: this.$t('amount'),
                  fontColor: flatColors.primary,
                  fontSize: 12
                }
              }, {
                id: 'y-axis-2',
                type: 'linear',
                position: 'right',
                gridLines: {
                  display: false,
                  drawBorder: false
                },
                ticks: {
                  fontColor: flatColors.secondary,
                  fontSize: 12,
                  padding: 10
                },
                scaleLabel: {
                  display: true,
                  labelString: this.$t('orders'),
                  fontColor: flatColors.secondary,
                  fontSize: 12
                }
              }]
            },
            legend: {
              display: true,
              position: 'top',
              align: 'end',
              labels: {
                boxWidth: 12,
                usePointStyle: true,
                padding: 20,
                fontColor: flatColors.darkGray,
                fontSize: 12
              }
            },
            tooltips: {
              backgroundColor: '#fff',
              titleFontSize: 13,
              titleFontColor: '#333',
              bodyFontColor: '#666',
              bodySpacing: 4,
              xPadding: 12,
              yPadding: 12,
              borderColor: 'rgba(0,0,0,0.1)',
              borderWidth: 1,
              displayColors: false,
              intersect: false,
              mode: 'index',
              callbacks: {
                label: (tooltipItem, data) => {
                  const label = data.datasets[tooltipItem.datasetIndex].label;
                  const value = tooltipItem.datasetIndex === 0 ? 
                    this.formatMoney(tooltipItem.yLabel) : 
                    tooltipItem.yLabel;
                  return ` ${label}: ${value}`;
                },
                labelColor: (tooltipItem, chart) => {
                  const dataset = chart.config.data.datasets[tooltipItem.datasetIndex];
                  return {
                    borderColor: 'transparent',
                    backgroundColor: dataset.borderColor
                  };
                }
              }
            },
            hover: {
              mode: 'index',
              intersect: false
            }
          }
        });
      });
    },
    
    // Optimized: Top products chart with better rendering
    renderTopProductsChart() {
      this.$nextTick(() => {
        const ctx = document.getElementById('topProductsChart');
        if (!ctx || !this.topSellingProducts.length) return;
        
        this.destroyChart(this.topProductsChart);
        
        const topProducts = this.topSellingProducts.slice(0, 5);
        const labels = topProducts.map(item => item.product?.label || 'Unknown Product');
        const data = topProducts.map(item => Number(item.total_quantity) || 0);
        
        this.topProductsChart = new Chart(ctx, {
          type: 'horizontalBar',
          data: {
            labels: labels,
            datasets: [{
              label: 'Quantity Sold',
              data: data,
              backgroundColor: [
                '#007bff',
                '#28a745', 
                '#ffc107',
                '#dc3545',
                '#6f42c1'
              ],
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            legend: {
              display: false
            },
            scales: {
              xAxes: [{
                ticks: {
                  beginAtZero: true
                }
              }]
            }
          }
        });
      });
    },
    

    
    formatDateTime(dateString) {
      if (!dateString) return '-';
      return moment(dateString).format('MMM DD, YYYY HH:mm');
    }
  },
  
  watch: {
    range(newVal) {
      if (!newVal || newVal[0] == null) {
        this.start = null;
        this.end = null;
      } else if (newVal.length === 2 && newVal[0] && newVal[1]) {
        this.start = moment(newVal[0]).format('YYYY-MM-DD');
        this.end = moment(newVal[1]).format('YYYY-MM-DD');
      } else {
        this.start = null;
        this.end = null;
      }
      this.handleDateChange();
    }
  },
  
  async mounted() {
    window.mixpanel.track("pos_insights");
    
    // Check permissions
    if (helper.checkAccess('view_pos') == -1) return this.$router.push({ path: '/' });
    if (helper.checkAccess('view_pos') == -2) return this.$router.push({ path: '/subscribe' });

      this.loadDashboardSettings();

    // Initialize this_month date range
    this.handleDateRangeChange();

    // Load Bootstrap icons if not already loaded
    if (!document.getElementById('bootstrap-icons')) {
      const link = document.createElement('link');
      link.id = 'bootstrap-icons';
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.10.0/font/bootstrap-icons.css';
      document.head.appendChild(link);
    }
    
    // Fetch all data using optimized method
    await this.refreshAllData();
  },
  
  beforeDestroy() {
    // Clean up chart instances
    this.destroyChart(this.paymentChart);
    this.destroyChart(this.dailySalesChart);
    this.destroyChart(this.topProductsChart);
    
    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },

  computed: {
    business() {
      return this.$store.state.business;
    }
  }
};
</script>

<style scoped>
.bg-gradient {
  background-image: linear-gradient(135deg, rgba(0,0,0,0.1), rgba(0,0,0,0.05));
}

.card {
  border-radius: 12px;
  overflow: hidden;
}

.card-header {
  border-radius: 12px 12px 0 0 !important;
  background: #f5f6fa !important;
}


.list-group-item {
  border-left: 3px solid transparent;
}

.badge {
  font-size: 0.75rem;
  padding: 0.5rem 0.75rem;
}

/* Chart containers */
canvas {
  max-height: 300px;
}

#dailySalesChart {
  height: 350px;
  width: 100%;
  min-height: 300px;
}

#paymentMethodChart {
  max-height: 250px;
}

#topProductsChart {
  max-height: 300px;
}



/* Responsive improvements */
@media (max-width: 768px) {
  .card-header h6 {
    font-size: 0.9rem;
  }
  
  .card-body h4 {
    font-size: 1.5rem;
  }
  
  .display-3 {
    font-size: 2rem;
  }
}

/* Loading animation */
.card-body {
  position: relative;
}

.card-body::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
  transform: translateX(-100%);
  animation: shimmer 1.5s infinite;
  z-index: 1;
  pointer-events: none;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

/* Custom scrollbar for lists */
.list-group {
  max-height: 400px;
  overflow-y: auto;
}

.list-group::-webkit-scrollbar {
  width: 6px;
}

.list-group::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.list-group::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.list-group::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Icon animations */
.bi {
  transition: transform 0.2s ease;
}

/* Enhanced metric cards */
.metric-card {
  background: linear-gradient(135deg, #fff, #f8f9fa);
  border: 1px solid rgba(0,0,0,0.05);
}

.metric-value {
  font-weight: 700;
  letter-spacing: -0.5px;
}

/* Chart card enhancements */
.chart-card {
  background: linear-gradient(135deg, #fff, #f8f9fa);
  border: 1px solid rgba(0,0,0,0.05);
}

.chart-card .card-body {
  padding: 1.5rem;
}
</style>
